import axios from "axios";
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_API_URL || "/api";
// Origin-only (no /api suffix) — for building absolute URLs to server-served
// static assets like /uploads/... that aren't API calls and so don't go
// through axios's baseURL. Empty when BASE_URL is the relative "/api"
// fallback (same-origin deployments), matching axios's own behavior there.
export const API_ORIGIN = BASE_URL === "/api" ? "" : BASE_URL;

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 60000,
});

// Attach JWT token (app auth) + Twitter userId if present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");   // login.tsx / callback.tsx use "authToken"
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  const userId = localStorage.getItem("twitterUserId");
  if (userId) config.headers["x-twitter-user-id"] = userId;
  try { const tz = Intl.DateTimeFormat().resolvedOptions().timeZone; if (tz) config.headers["X-User-Timezone"] = tz; } catch { /* no Intl: server falls back to UTC */ }
  return config;
});

// Surface server 409 (conflict) messages as user-facing toasts, and trigger
// the global "add an API key or upgrade" gate modal (mounted once at the
// app root — see ApiKeyGateModal.tsx / App.tsx) whenever a route blocked by
// middleware/llmQuota.js on the server rejects a request. A plain
// CustomEvent is used rather than importing React context here, since this
// file has no component tree of its own — matches the same
// dispatchEvent/addEventListener pattern already used elsewhere in the app
// (e.g. ConversationsPage.jsx's 'chats:updated').
api.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const msg = data?.error || error?.message;
      if (data?.code === 'API_KEY_REQUIRED') {
        window.dispatchEvent(new CustomEvent('lyra:api-key-required', { detail: data }));
      } else if (status === 409 && msg) {
        // show server-provided message (e.g. "Duplicate Lyran! not allowed..")
        toast.error(msg);
      }
    } catch (e) {
      // ignore toast/event failures
    }
    return Promise.reject(error);
  }
);


// ── Config ───────────────────────────────────────────────────
export const getServerConfig = () => api.get("/config").then((r) => r.data);

// ── AI Agents ────────────────────────────────────────────────
export const agentAPI = {
  getTasks: () => api.get("/api/agents/tasks").then((r) => r.data),
  getModels: () => api.get("/api/agents/models").then((r) => r.data),
  getModelsWithPrefs: () => {
    if (!agentAPI._pending) agentAPI._pending = new Map();
    const key = 'modelsWithPrefs';
    if (agentAPI._pending.has(key)) return agentAPI._pending.get(key);
    const p = api.get("/api/agents/models-with-prefs").then((r) => r.data).finally(() => { try { agentAPI._pending.delete(key); } catch (e) {} });
    agentAPI._pending.set(key, p);
    return p;
  },
  testModel: (modelId, apiKey) =>
    api.post("/api/agents/test-model", { modelId, apiKey }).then((r) => r.data),
  run: (payload) => api.post("/api/agents/run", payload).then((r) => r.data),
  getHistory: (params) => api.get("/api/agents/history", { params }).then((r) => r.data),
  getStats: () => api.get("/api/agents/stats").then((r) => r.data),
  clone: (agentId) => api.post(`/api/agents/clone/${agentId}`).then(r => r.data),
  // Model preferences — server-side per-user persistence
  getModelPreferences: () => api.get("/api/agents/model-preferences").then((r) => r.data),
  saveModelPreferences: (prefs) => api.put("/api/agents/model-preferences", prefs).then((r) => r.data),
  // Custom API keys — encrypted at rest on server
  saveCustomApiKeys: (keys) => api.put("/api/agents/custom-api-keys", { keys }).then((r) => r.data),
  // Engram memory key — encrypted at rest on server
  getEngramKey: () => api.get("/api/agents/engram-key").then((r) => r.data),
  saveEngramKey: (key) => api.put("/api/agents/engram-key", { key }).then((r) => r.data),
  // Engram chat memory toggle
  saveEngramSettings: (settings) => api.put("/api/agents/engram-settings", settings).then((r) => r.data),

  // ── Model catalog (provider/model picker for Add Model modal) ──────────────
  /** Returns the provider+model catalog merged with the user's saved custom models */
  getModelCatalog: () => api.get("/api/models/catalog").then((r) => r.data),

  /** Persist a custom (or known catalog) model entry for the current user */
  saveUserModel: (model) => api.post("/api/models/user-models", model).then((r) => r.data),

  /** Delete a user-saved model entry by its MongoDB _id */
  deleteUserModel: (id) => api.delete(`/api/models/user-models/${id}`).then((r) => r.data),
};


// ── Twitter ──────────────────────────────────────────────────
export const twitterAPI = {
  getMe: () => api.get("/api/twitter/me").then((r) => r.data),
  getTimeline: (limit = 10) =>
    api.get("/api/twitter/timeline", { params: { limit } }).then((r) => r.data),
  tweet: (text) => api.post("/api/twitter/tweet", { text }).then((r) => r.data),
  updateHeader: (base64) =>
    api.post("/api/twitter/header", { base64 }).then((r) => r.data),
  verifyCredentials: (creds) =>
    api.post("/api/twitter/verify-credentials", creds).then((r) => r.data),
  // Credential management (Connectors form)
  saveCredentials: (creds) =>
    api.post("/api/twitter/credentials", creds).then((r) => r.data),
  credentialsStatus: () =>
    api.get("/api/twitter/credentials/status").then((r) => r.data),
  removeCredentials: () =>
    api.delete("/api/twitter/credentials").then((r) => r.data),
};

// ── Instagram (Meta) ─────────────────────────────────────────────────────
export const instagramAPI = {
  connect: () => api.post('/auth/instagram').then(r => r.data), // returns { authUrl }
  status: () => api.get('/auth/instagram/status').then(r => r.data),
  disconnect: () => api.delete('/auth/instagram/disconnect').then(r => r.data),
};

// ── LinkedIn
export const linkedinAPI = {
  connect: () => {
    // If user is currently logged in, pass the session token in state so
    // the backend can attach LinkedIn to the existing account instead of
    // issuing a new login token.
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const state = JSON.stringify({ token });
        return api.post('/auth/linkedin', { state }).then(r => r.data);
      }
    } catch (e) {
      // ignore errors reading localStorage
    }
    return api.post('/auth/linkedin').then(r => r.data);
  },
  status: () => api.get('/auth/linkedin/status').then(r => r.data),
  pages: () => api.get('/auth/linkedin/pages').then(r => r.data),
  disconnect: () => api.delete('/auth/linkedin/disconnect').then(r => r.data),
  post: (payload) => api.post('/auth/linkedin/post', payload).then(r => r.data),
};

// ── Skills Catalogue ─────────────────────────────────────────
export const skillsCatalogueAPI = {
  list: (params) => api.get('/api/skills/catalogue', { params }).then(r => r.data),
};

// ── Scheduler ────────────────────────────────────────────────
export const schedulerAPI = {
  getPosts: (params) => api.get("/api/scheduler/posts", { params }).then((r) => r.data),
  getStatus: () => api.get("/api/scheduler/status").then((r) => r.data),
  createPost: (payload) => api.post("/api/scheduler/posts", payload).then((r) => r.data),
  updatePost: (id, payload) =>
    api.put(`/api/scheduler/posts/${id}`, payload).then((r) => r.data),
  cancelPost: (id) => api.delete(`/api/scheduler/posts/${id}`).then((r) => r.data),
  publishNow: (id) =>
    api.post(`/api/scheduler/posts/${id}/publish-now`).then((r) => r.data),
};

// ── Custom Agents ─────────────────────────────────────────────
export const customAgentAPI = {
  // ── Agents ────────────────────────────────────────────────────────────────
  list: () => api.get("/api/custom-agents").then(r => r.data),
  create: (data) => api.post("/api/custom-agents", data).then(r => r.data),
  update: (id, d) => api.put(`/api/custom-agents/${id}`, d).then(r => r.data),
  remove: (id) => api.delete(`/api/custom-agents/${id}`).then(r => r.data),
  execute: (id, d) => api.post(`/api/custom-agents/${id}/execute`, d, { timeout: 300000 }).then(r => r.data),
  checkSimilarity: (data) => api.post("/api/custom-agents/check-similarity", data).then(r => r.data),

  // ── Chains ────────────────────────────────────────────────────────────────
  listChains: () => api.get("/api/custom-agents/chains").then(r => r.data),
  createChain: (data) => api.post("/api/custom-agents/chains", data).then(r => r.data),
  updateChain: (id, d) => api.put(`/api/custom-agents/chains/${id}`, d).then(r => r.data),
  removeChain: (id) => api.delete(`/api/custom-agents/chains/${id}`).then(r => r.data),
  checkChainSimilarity: (data) => api.post("/api/custom-agents/chains/check-similarity", data).then(r => r.data),

  // ── Flow canvas helpers (source:"canvas") ─────────────────────────────────
  // Save a brand-new canvas flow → creates a chain with source:"canvas"
  saveFlow: (flowName, nodes, connections) =>
    api.post("/api/custom-agents/chains", {
      name: flowName,
      description: `Canvas flow: ${flowName}`,
      icon: "🔀",
      source: "canvas",
      steps: [],          // executor uses canvasData for canvas chains
      canvasData: { nodes, connections },
    }).then(r => r.data),

  // Update an existing canvas flow (re-save after edits)
  updateFlow: (id, flowName, nodes, connections) =>
    api.put(`/api/custom-agents/chains/${id}`, {
      name: flowName,
      source: "canvas",
      steps: [],
      canvasData: { nodes, connections },
    }).then(r => r.data),

  // Execute a canvas flow — returns { outputs, nodeResults }
  runFlow: (chainId, nodes, connections, globalInput = "Run flow", chainName = '', modelId) =>
    api.post(`/api/custom-agents/chains/${chainId}/run-flow`, {
      nodes, connections, globalInput, chainName, modelId,
    }).then(r => r.data),

  wizardChat: (messages, system, modelId, customApiKey) =>
    api.post("/api/agents/wizard-chat", { messages, system, modelId, customApiKey }).then(r => r.data),

    // ── Marketplace ───────────────────────────────────────────────────────────
  getMarketplace: () => 
    api.get("/api/custom-agents/marketplace").then(r => r.data),

  togglePublish: (agentId) => 
    api.patch(`/api/custom-agents/${agentId}/publish`).then(r => r.data),

  addFromMarketplace: (agentId) => 
    api.post(`/api/custom-agents/marketplace/${agentId}/add`).then(r => r.data),

  rateAgent: (agentId, rating) =>
    api.post(`/api/custom-agents/marketplace/${agentId}/rate`, { rating }).then(r => r.data),

  getTemplates: () => 
    api.get("/api/custom-agents/templates").then(r => r.data),
  getGraph: (agentId) =>
    api.get(`/api/custom-agents/${agentId}/graph`).then(r => r.data),
  inspect: (id) => api.get(`/api/custom-agents/${id}/inspect`).then(r => r.data),
};

// ── Gmail ─────────────────────────────────────────────────────
export const gmailAPI = {
  status: () => api.get("/api/gmail/status").then((r) => r.data),
  connect: () => api.get("/api/gmail/connect").then((r) => r.data),
  send: (payload) => api.post("/api/gmail/send", payload).then((r) => r.data),
  disconnect: () => api.delete("/api/gmail/disconnect").then((r) => r.data),
  listMessages: (params) => api.get('/api/gmail/messages', { params }).then(r => r.data),
  getMessage: (id, params) => api.get(`/api/gmail/messages/${id}`, { params }).then(r => r.data),
};

export const integrationRequestAPI = {
  send: (text) => api.post('/api/integration-requests', { text }).then(r => r.data),
};

export const youtubeAPI = {
  status: () => api.get('/api/youtube/status').then(r => r.data),
  connect: () => api.get('/api/youtube/connect').then(r => r.data),
  test: () => api.get('/api/youtube/test').then(r => r.data),
  disconnect: () => api.delete('/api/youtube/disconnect').then(r => r.data),
};

// ── Calendar (Google) ─────────────────────────────────────────────
export const calendarAPI = {
  status: () => api.get('/api/calendar/status').then(r => r.data),
  connect: () => api.get('/api/calendar/connect').then(r => r.data),
  disconnect: () => api.delete('/api/calendar/disconnect').then(r => r.data),
  listEvents: (params) => api.get('/api/calendar/events', { params }).then(r => r.data),
  createEvent: (payload) => api.post('/api/calendar/events', payload).then(r => r.data),
  sync: () => api.post('/api/calendar/sync').then(r => r.data),
};

// ── Flow Schedules ─────────────────────────────────────────────
export const flowScheduleAPI = {
  listForFlow: (flowDbId) =>
    api.get(`/api/flow-schedules/flow/${flowDbId}`).then((r) => r.data),
  listAll: () =>
    api.get("/api/flow-schedules").then((r) => r.data),
  create: (payload) =>
    api.post("/api/flow-schedules", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/api/flow-schedules/${id}`, payload).then((r) => r.data),
  // Stop the cron job but keep the schedule record (can re-enable later)
  cancel: (id) =>
    api.put(`/api/flow-schedules/${id}`, { enabled: false }).then((r) => r.data),
  // Permanently delete the schedule and stop the cron job
  remove: (id) =>
    api.delete(`/api/flow-schedules/${id}`).then((r) => r.data),
};


// ── Chat Sessions + Folders ───────────────────────────────────
export const chatSessionAPI = {
  // Sessions
  list:    ()           => api.get('/api/chat-sessions').then(r => r.data),
  create:  (data)       => api.post('/api/chat-sessions', data).then(r => r.data),
  get:     (id)         => api.get(`/api/chat-sessions/${id}`).then(r => r.data),
  update:  (id, data)   => api.put(`/api/chat-sessions/${id}`, data).then(r => r.data),
  remove:  (id)         => api.delete(`/api/chat-sessions/${id}`).then(r => r.data),
  message: (id, data)   => api.post(`/api/chat-sessions/${id}/message`, data, { timeout: 300000 }).then(r => r.data),

  // Folders (sub-routes of same router)
  listFolders:   ()         => api.get('/api/chat-sessions/folders').then(r => r.data),
  createFolder:  (data)     => api.post('/api/chat-sessions/folders', data).then(r => r.data),
  updateFolder:  (id, data) => api.put(`/api/chat-sessions/folders/${id}`, data).then(r => r.data),
  removeFolder:  (id)       => api.delete(`/api/chat-sessions/folders/${id}`).then(r => r.data),
};

// ── Sales leads ("Contact Sales" — no self-serve payment flow yet) ────────────
export const salesLeadAPI = {
  create: (note) => api.post('/api/sales-leads', { note }).then(r => r.data),
};

// ── Git (GitHub / GitLab) tokens ──────────────────────────────
export const gitAPI = {
  githubStatus:      ()      => api.get('/api/git/github/status').then(r => r.data),
  saveGithubToken:   (token) => api.post('/api/git/github/token', { token }).then(r => r.data).then(d => {
    // Cache plaintext token in localStorage so repo connect can use it directly
    localStorage.setItem('githubToken', token);
    return d;
  }),
  removeGithubToken: ()      => api.delete('/api/git/github/token').then(r => r.data).then(d => {
    localStorage.removeItem('githubToken');
    return d;
  }),
  gitlabStatus:      ()      => api.get('/api/git/gitlab/status').then(r => r.data),
  saveGitlabToken:   (token) => api.post('/api/git/gitlab/token', { token }).then(r => r.data).then(d => {
    localStorage.setItem('gitlabToken', token);
    return d;
  }),
  removeGitlabToken: ()      => api.delete('/api/git/gitlab/token').then(r => r.data).then(d => {
    localStorage.removeItem('gitlabToken');
    return d;
  }),
};

// ── MCP Servers ──────────────────────────────────────────────────────────────
export const mcpAPI = {
  listServers:  ()           => api.get('/api/mcp/servers').then(r => r.data),
  addServer:    (payload)    => api.post('/api/mcp/servers', payload).then(r => r.data),
  updateServer: (id, data)   => api.put(`/api/mcp/servers/${id}`, data).then(r => r.data),
  deleteServer: (id)         => api.delete(`/api/mcp/servers/${id}`).then(r => r.data),
  testServer:   (id)         => api.post(`/api/mcp/servers/${id}/test`).then(r => r.data),
  startOAuth:   (id)         => api.post(`/api/mcp/servers/${id}/oauth/start`).then(r => r.data),
  getTools:     (id)         => api.get(`/api/mcp/servers/${id}/tools`).then(r => r.data),
  callTool:     (toolName, args) => api.post('/api/mcp/call', { toolName, args }).then(r => r.data),
  // Figma PAT
  figmaPatStatus: ()         => api.get('/api/mcp/figma/pat/status').then(r => r.data),
  figmaPatSave:   (token)    => api.post('/api/mcp/figma/pat', { token }).then(r => r.data),
  figmaPatDelete: ()         => api.delete('/api/mcp/figma/pat').then(r => r.data),
};

export const externalAgentsAPI = {
  list: () => api.get('/api/external-agents').then(r => r.data),
  create: (payload) => api.post('/api/external-agents', payload).then(r => r.data),
  startOAuth: (id) => api.post(`/api/external-agents/${id}/oauth/start`).then(r => r.data),
  update: (id, payload) => api.put(`/api/external-agents/${id}`, payload).then(r => r.data),
  test: (id) => api.post(`/api/external-agents/${id}/test`).then(r => r.data),
  remove: (id) => api.delete(`/api/external-agents/${id}`).then(r => r.data),
  audit: (id) => api.get(`/api/external-agents/${id}/audit`).then(r => r.data),
};

export const personalMcpAPI = {
  listKeys:     ()     => api.get('/api/user/api-keys').then(r => r.data),
  createKey:    ()     => api.post('/api/user/api-keys', {}).then(r => r.data),
  deleteKey:    (id)   => api.delete(`/api/user/api-keys/${id}`).then(r => r.data),
  listTools:    ()     => api.get('/api/user/api-keys/tools').then(r => r.data),
};

export const agentRunAPI = {
  createRun:    (payload) => api.post('/api/agent-runs', payload).then(r => r.data),
  getActive:    ()        => api.get('/api/agent-runs/active').then(r => r.data),
  stop:         (runId)   => api.post(`/api/agent-runs/${runId}/stop`).then(r => r.data),
  getRun:       (runId)   => api.get(`/api/agent-runs/${runId}`).then(r => r.data),
  listAll:      (params)  => api.get('/api/agent-runs', { params }).then(r => r.data),
  // One-time scheduling
  scheduleRun:  (payload) => api.post('/api/agent-runs/schedule', payload).then(r => r.data),
  getScheduled: ()        => api.get('/api/agent-runs/scheduled').then(r => r.data),
  cancelScheduled: (runId) => api.delete(`/api/agent-runs/${runId}`).then(r => r.data),
  rescheduleRun: (runId, scheduledAt) => api.patch(`/api/agent-runs/${runId}/reschedule`, { scheduledAt }).then(r => r.data),
};

// ── Runtime API ─────────────────────────────────────────────────────
export const runtimeAPI = {
  getTasks: () => api.get('/api/runtime/tasks').then(r => r.data),
  stopTask: (taskId) => api.post(`/api/runtime/stop/${taskId}`).then(r => r.data),
};

// ── Evals ───────────────────────────────────────────────────────────
export const evalsAPI = {
  suites:       ()                 => api.get('/api/evals/suites').then(r => r.data),
  addCase:      (suiteId, c)       => api.post(`/api/evals/suites/${suiteId}/cases`, c).then(r => r.data),
  updateCase:   (suiteId, id, c)   => api.put(`/api/evals/suites/${suiteId}/cases/${id}`, c).then(r => r.data),
  deleteCase:   (suiteId, id)      => api.delete(`/api/evals/suites/${suiteId}/cases/${id}`).then(r => r.data),
  startRun:     (payload)          => api.post('/api/evals/runs', payload).then(r => r.data),
  listRuns:     (params)           => api.get('/api/evals/runs', { params }).then(r => r.data),
  getRun:       (id)               => api.get(`/api/evals/runs/${id}`).then(r => r.data),
  cancelRun:    (id)               => api.post(`/api/evals/runs/${id}/cancel`).then(r => r.data),
  runChecks:    (runId)            => api.get(`/api/evals/agent-runs/${runId}/checks`).then(r => r.data),
  retryExternalEvaluation: (runId) => api.post(`/api/evals/agent-runs/${runId}/external-evaluation/retry`).then(r => r.data),
  runTrace:     (runId)            => api.get(`/api/evals/agent-runs/${runId}/trace`).then(r => r.data),
  feedback:     (runId, rating, note) => api.post(`/api/evals/agent-runs/${runId}/feedback`, { rating, note }).then(r => r.data),
  judge:        (runId, rubric, evalHarnessRequired) => api.post(`/api/evals/agent-runs/${runId}/judge`, { rubric, ...(typeof evalHarnessRequired === 'boolean' ? { evalHarnessRequired } : {}) }).then(r => r.data),
  runToCase:    (runId, name)      => api.post(`/api/evals/agent-runs/${runId}/to-case`, { name }).then(r => r.data),
};

export const remindersAPI = {
  list: (params = {}) => api.get('/api/reminders', { params }).then(r => r.data),
};


export const onboardingAPI = {
  get:      ()        => api.get('/api/user/onboarding').then(r => r.data),
  save:     (payload) => api.post('/api/user/onboarding', payload).then(r => r.data),
  updateDomains: (domains, skillIdsByDomain) => api.put('/api/user/onboarding/domains', { domains, skillIdsByDomain }).then(r => r.data),
  toggleSkill: (domain, skillId, action) => api.post('/api/user/onboarding/skill', { domain, skillId, action }).then(r => r.data),
};

// ── Default Agents (onboarding catalogue expansion) ──────────────────────────
export const defaultAgentsAPI = {
  /** Returns { agents: AgentDescriptor[], skillIds: string[] } for the user's onboarding picks */
  get: () => api.get('/api/user/default-agents').then(r => r.data),
};

// ── Domain agent services (Engineering -> AgenticSDLC) ───────────────────────
export const domainAgentsAPI = {
  /** Returns { domains: [{ domain, label, service }] } — the domains that have a remote agent service */
  domains: () => api.get('/api/domain-agents').then(r => r.data),
  /** Returns { domain, service, agents: DomainAgent[], fetchedAt } from the remote domain's agent registry */
  /** Runs one agent on its domain's service. Returns the service's result: { agentId, status, output, nextActions?, ... } */
  run: (domain, agentId, input = {}) =>
    api.post(`/api/domain-agents/${encodeURIComponent(domain)}/agents/${encodeURIComponent(agentId)}/run`, { input }).then(r => r.data),
  list: (domain, { refresh = false } = {}) =>
    api.get(`/api/domain-agents/${encodeURIComponent(domain)}`, { params: refresh ? { refresh: 1 } : undefined }).then(r => r.data),
  /** HR-only multi-turn chat, backed by AgenticHR's real Agentic Mode. `messages` is the
   * full running history ({role, content}[]); returns { reply, actions, conversation_id }. */
  chat: (messages, conversationId) =>
    api.post('/api/domain-agents/hr/chat', { messages, conversationId }).then(r => r.data),
};

// ── AgenticHR org link (which AgenticHR organization the HR domain agent acts for) ──────────
export const agenticHrAPI = {
  /** Returns { organizations: [{id, name}] } — this user's email's AgenticHR orgs */
  organizations: () => api.get('/api/agentic-hr/organizations').then(r => r.data),
  /** Returns { link: {organizationId, organizationName, linkedAt} | null } */
  getLink: () => api.get('/api/agentic-hr/link').then(r => r.data),
  link: (organizationId, organizationName) =>
    api.post('/api/agentic-hr/link', { organizationId, organizationName }).then(r => r.data),
  unlink: () => api.delete('/api/agentic-hr/link').then(r => r.data),
  /** Returns { connection: null | {organizationId, organizationName, connected}, appUrl } */
  status: () => api.get('/api/agentic-hr/status', { timeout: 45000 }).then(r => r.data),
  /** Live MCP check. Returns { ok, toolCount, tools, error, tokenExpiresAt } */
  verify: () => api.get('/api/agentic-hr/verify', { timeout: 45000 }).then(r => r.data),
  /** Creates a Workforce org owned by this user. Returns { organization: {id, name} } */
  createOrganization: (organizationName) =>
    api.post('/api/agentic-hr/organizations', { organizationName }).then(r => r.data),
  /** Connects Workforce (MCP) for the HR domain. Returns { connection } */
  connect: (organizationId, organizationName) =>
    api.post('/api/agentic-hr/connect', { organizationId, organizationName }, { timeout: 60000 }).then(r => r.data),
};

// ── AgenticFinance org link (the Finance domain agent's organization) ─────────
export const agenticFinanceAPI = {
  /** Removes the linked Finance organization and its MCP server for this user. */
  unlink: () => api.delete('/api/agentic-finance/link').then(r => r.data),
};

/** Domain agents Core can connect to (server/src/services/connectors). */
export const connectorsAPI = {
  /** { connectors: [{ id, domain, label, auth: 'oauth2' | 'service', connected, error }] } */
  list: () => api.get('/api/connectors').then(r => r.data),
  /** oauth2: { mode: 'oauth2', authorizeUrl } (send the browser there); service: { mode: 'service' } */
  connect: (id) => api.post(`/api/connectors/${encodeURIComponent(id)}/connect`, {}, { timeout: 60000 }).then(r => r.data),
  disconnect: (id) => api.delete(`/api/connectors/${encodeURIComponent(id)}`).then(r => r.data),
};

/** MongoDB / AWS S3 / Cloudflare R2 connections. Credentials are tested, then stored encrypted; they are never returned. */
export const dataConnectionsAPI = {
  /** { connections: [{ provider, config, verifiedAt }] } */
  list: () => api.get('/api/data-connections').then(r => r.data),
  save: (provider, values) => api.put(`/api/data-connections/${encodeURIComponent(provider)}`, values, { timeout: 30000 }).then(r => r.data),
  remove: (provider) => api.delete(`/api/data-connections/${encodeURIComponent(provider)}`).then(r => r.data),
};

/** Proof that the account's email address is the user's own. Connecting a domain agent requires it. */
export const emailVerificationAPI = {
  /** { verified, email } */
  status: () => api.get('/api/email-verification/status').then(r => r.data),
};

export const userAPI = {
  getUserPreferences: () => {
    if (!userAPI._pending) userAPI._pending = new Map();
    const key = 'userPreferences';
    if (userAPI._pending.has(key)) return userAPI._pending.get(key);
    const p = api.get('/api/user/preferences').then(r => r.data).finally(() => { try { userAPI._pending.delete(key); } catch (e) {} });
    userAPI._pending.set(key, p);
    return p;
  },
  updateUserPreferences: (payload) => api.patch('/api/user/preferences', payload).then(r => r.data),
  checkLyranName: (name) => api.get('/api/user/preferences/check-lyran', { params: { name } }).then(r => r.data),
};

export const userSkillsAPI = {
  list: () => api.get('/api/user-skills').then(r => r.data),
  create: (payload) => api.post('/api/user-skills', payload).then(r => r.data),
  update: (id, payload) => api.put(`/api/user-skills/${id}`, payload).then(r => r.data),
  remove: (id) => api.delete(`/api/user-skills/${id}`).then(r => r.data),
  listPublic: (page = 1, limit = 20) => api.get('/api/user-skills/public/list', { params: { page, limit } }).then(r => r.data),
};

// ── Skills registry API (install/uninstall/config/test) ─────────────────
export const skillsAPI = {
  list: () => api.get('/api/skills').then(r => r.data),
  install: (skillId) => api.post(`/api/skills/${skillId}/install`).then(r => r.data),
  uninstall: (skillId) => api.delete(`/api/skills/${skillId}/uninstall`).then(r => r.data),
  config: (skillId, config) => api.put(`/api/skills/${skillId}/config`, { config }).then(r => r.data),
  test: (skillId, payload) => api.post(`/api/skills/${skillId}/test`, payload).then(r => r.data),
};

export const communityAPI = {
  getOnlineMembers: () => {
    if (!communityAPI._pending) communityAPI._pending = new Map();
    const key = 'online';
    if (communityAPI._pending.has(key)) return communityAPI._pending.get(key);
    const p = api.get('/api/community/online').then(r => r.data).finally(() => { try { communityAPI._pending.delete(key); } catch (e) {} });
    communityAPI._pending.set(key, p);
    return p;
  },
  getMembers: (params) => {
    if (!communityAPI._pending) communityAPI._pending = new Map();
    const key = `members:${JSON.stringify(params || {})}`;
    if (communityAPI._pending.has(key)) return communityAPI._pending.get(key);
    const p = api.get('/api/community/members', { params }).then(r => r.data).finally(() => { try { communityAPI._pending.delete(key); } catch (e) {} });
    communityAPI._pending.set(key, p);
    return p;
  },
  getMemberProfile: (userId) => api.get(`/api/community/members/${userId}`).then(r => r.data),
  getFollowers: (userId) => api.get(`/api/community/members/${userId}/followers`).then(r => r.data),
  toggleFollow: (userId) => api.post(`/api/community/follow/${userId}`).then(r => r.data),
  rateLyran: (userId, value) => api.post(`/api/community/rate/${userId}`, { value }).then(r => r.data),
  getMyProfile: () => api.get('/api/community/profile').then(r => r.data),
  updateMyProfile: (payload) => api.patch('/api/community/profile', payload).then(r => r.data),
  getRecentMembers: () => {
    if (!communityAPI._pending) communityAPI._pending = new Map();
    const key = 'recent';
    if (communityAPI._pending.has(key)) return communityAPI._pending.get(key);
    const p = api.get('/api/community/recent').then(r => r.data).finally(() => { try { communityAPI._pending.delete(key); } catch (e) {} });
    communityAPI._pending.set(key, p);
    return p;
  },
  getTrendingAgents: () => api.get('/api/community/trending-agents').then(r => r.data),
  getStreak: () => api.get('/api/community/streak').then(r => r.data),
};

export const spacesAPI = {
  list:          (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''))
    ).toString();
    return api.get(`/api/spaces${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  // Deduplicate concurrent GETs for the same spaceId to avoid multiple network calls
  get: (spaceId) => {
    if (!spacesAPI._pendingGets) spacesAPI._pendingGets = new Map();
    const key = String(spaceId);
    if (spacesAPI._pendingGets.has(key)) return spacesAPI._pendingGets.get(key);
    const p = api.get(`/api/spaces/${spaceId}`).then(r => r.data).finally(() => {
      try { spacesAPI._pendingGets.delete(key); } catch (_) {}
    });
    spacesAPI._pendingGets.set(key, p);
    return p;
  },
  create:        (payload)           => api.post('/api/spaces', payload).then(r => r.data),
  join:          (spaceId)           => api.post(`/api/spaces/${spaceId}/join`).then(r => r.data),
  requestJoin:   (spaceId, message)  => api.post(`/api/spaces/${spaceId}/request`, { message }).then(r => r.data),
  leave:         (spaceId)           => api.post(`/api/spaces/${spaceId}/leave`).then(r => r.data),
  invite:        (spaceId, userId, role) => api.post(`/api/spaces/${spaceId}/invite`, { userId, role }).then(r => r.data),
  declineInvite: (spaceId, inviteId) => api.post(`/api/spaces/${spaceId}/invite/${inviteId}/decline`).then(r => r.data),
  cancelInvite:  (spaceId, inviteId) => api.post(`/api/spaces/${spaceId}/invite/${inviteId}/cancel`).then(r => r.data),
  update:        (spaceId, payload)  => api.patch(`/api/spaces/${spaceId}`, payload).then(r => r.data),
  availableConnectors: () => api.get('/api/spaces/connectors/available').then(r => r.data),
  delete:        (spaceId)           => api.delete(`/api/spaces/${spaceId}`).then(r => r.data),
  removeMember:  (spaceId, userId)   => api.delete(`/api/spaces/${spaceId}/members/${userId}`).then(r => r.data),
  setMemberRole: (spaceId, userId, role) => api.patch(`/api/spaces/${spaceId}/members/${userId}/role`, { role }).then(r => r.data),
  // Team agents: the roster of agents members share, the owner's controls, requests to use a teammate's agent
  agents:        (spaceId)           => api.get(`/api/spaces/${spaceId}/agents`).then(r => r.data),
  addAgent:      (spaceId, agentId, access, payer) => api.post(`/api/spaces/${spaceId}/agents`, { agentId, access, ...(payer ? { payer } : {}) }).then(r => r.data),
  myAgents:      (spaceId)           => api.get(`/api/spaces/${spaceId}/my-agents`).then(r => r.data),
  setAgentAccess:(spaceId, rosterId, access) => api.patch(`/api/spaces/${spaceId}/agents/${rosterId}`, { access }).then(r => r.data),
  blockAgent:    (spaceId, rosterId, reason) => api.post(`/api/spaces/${spaceId}/agents/${rosterId}/block`, { reason }).then(r => r.data),
  unblockAgent:  (spaceId, rosterId) => api.post(`/api/spaces/${spaceId}/agents/${rosterId}/unblock`).then(r => r.data),
  removeAgent:   (spaceId, rosterId) => api.delete(`/api/spaces/${spaceId}/agents/${rosterId}`).then(r => r.data),
  requestAgent:  (spaceId, rosterId, prompt, acceptCost = false) => api.post(`/api/spaces/${spaceId}/agents/${rosterId}/request`, { prompt, acceptCost }).then(r => r.data),
  setAgentPayer: (spaceId, rosterId, payer) => api.patch(`/api/spaces/${spaceId}/agents/${rosterId}`, { payer }).then(r => r.data),
  requestSponsor:(spaceId, rosterId, prompt) => api.post(`/api/spaces/${spaceId}/agents/${rosterId}/sponsor-request`, { prompt }).then(r => r.data),
  approveSponsorRequest: (spaceId, requestId, capTokens) => api.post(`/api/spaces/${spaceId}/sponsor-requests/${requestId}/approve`, { capTokens }).then(r => r.data),
  declineSponsorRequest: (spaceId, requestId) => api.post(`/api/spaces/${spaceId}/sponsor-requests/${requestId}/decline`).then(r => r.data),
  usage:         (spaceId, params = {}) => api.get(`/api/spaces/${spaceId}/usage`, { params }).then(r => r.data),
  approveAgentRequest: (spaceId, requestId, always = false) => api.post(`/api/spaces/${spaceId}/agent-requests/${requestId}/approve`, { always }).then(r => r.data),
  declineAgentRequest: (spaceId, requestId) => api.post(`/api/spaces/${spaceId}/agent-requests/${requestId}/decline`).then(r => r.data),
  blockLyran:    (spaceId, userId, reason) => api.post(`/api/spaces/${spaceId}/members/${userId}/lyran/block`, { reason }).then(r => r.data),
  unblockLyran:  (spaceId, userId) => api.post(`/api/spaces/${spaceId}/members/${userId}/lyran/unblock`).then(r => r.data),
  setAutoReply:  (spaceId, enabled) => api.patch(`/api/spaces/${spaceId}/members/me/auto-reply`, { enabled }).then(r => r.data),
  typing:        (spaceId, typing = true) => api.post(`/api/spaces/${spaceId}/typing`, { typing }).then(r => r.data).catch(() => null),
  // Kanban-synced Space tasks
  listKanbanTasks:  (spaceId)          => api.get(`/api/spaces/${spaceId}/kanban-tasks`).then(r => r.data),
  createKanbanTask: (spaceId, payload) => api.post(`/api/spaces/${spaceId}/kanban-tasks`, payload).then(r => r.data),
  // Files
  listFiles:  (spaceId)         => api.get(`/api/spaces/${spaceId}/files`).then(r => r.data),
  uploadFile: (spaceId, file, activityId) => {
    const form = new FormData();
    form.append('file', file);
    if (activityId) form.append('activityId', activityId);
    // Don't set Content-Type manually — axios/the browser must set it with the multipart boundary
    return api.post(`/api/spaces/${spaceId}/files`, form).then(r => r.data);
  },
  deleteFile: (spaceId, fileId) => api.delete(`/api/spaces/${spaceId}/files/${fileId}`).then(r => r.data),
  // Room
  getActivity: (spaceId, params = {}) => {
    if (!spacesAPI._pendingActivityGets) spacesAPI._pendingActivityGets = new Map();
    const key = `${spaceId}::${JSON.stringify(params || {})}`;
    if (spacesAPI._pendingActivityGets.has(key)) return spacesAPI._pendingActivityGets.get(key);
    const p = api.get(`/api/spaces/${spaceId}/activity`, { params }).then(r => r.data).finally(() => {
      try { spacesAPI._pendingActivityGets.delete(key); } catch (_) {}
    });
    spacesAPI._pendingActivityGets.set(key, p);
    return p;
  },
  // postMessage accepts either a string (content) or an object payload ({ content, modelId, mentions, ... })
  postMessage:   (spaceId, payload)     => {
    if (typeof payload === 'string') return api.post(`/api/spaces/${spaceId}/activity`, { content: payload }).then(r => r.data);
    // if payload is an object, send it directly as the request body so fields like modelId and mentions are top-level
    return api.post(`/api/spaces/${spaceId}/activity`, payload).then(r => r.data);
  },
  // task APIs and stopAll removed — unused in client
};

// ── Usage / Cost & Budget ────────────────────────────────────────────────────
export const usageAPI = {
  getCostSummary:      ()               => api.get('/api/usage/cost-summary').then(r => r.data),
  getBalances:         ()               => api.get('/api/usage/balances').then(r => r.data),
  listCredits:         ()               => api.get('/api/usage/credits').then(r => r.data),
  addCredit:           (payload)        => api.post('/api/usage/credits', payload).then(r => r.data),
  deleteCredit:        (id)             => api.delete(`/api/usage/credits/${id}`).then(r => r.data),
  getBudgetSettings:   ()               => api.get('/api/usage/budget-settings').then(r => r.data),
  updateBudgetSettings:(payload)        => api.put('/api/usage/budget-settings', payload).then(r => r.data),
};

export default api;
